import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { createIdpClient, bindIdpClient } from '@digiwedge/idp-client';
import { resolveAudience } from '@digiwedge/hooks-auth-web';
import { useGlobalStore } from '@digiwedge/global-state';
import { setTeeTimeAuthTokenProvider } from '@digiwedge/teetime-api-client';
import { authBoot, scheduleProactiveRefresh, persist, decodeExp } from './auth-boot.js';
import { makeAuthChannel } from './auth-channel.js';
import { resetRefreshBlock } from './refresh-guard.js';
const IdpContext = createContext(null);
setTeeTimeAuthTokenProvider(() => useGlobalStore.getState().token ?? undefined);
function resolveMetaEnv() {
    if (typeof globalThis !== 'undefined') {
        const injected = globalThis.__viteImportMetaEnv__;
        if (injected)
            return injected;
    }
    try {
        // eslint-disable-next-line no-new-func
        return new Function('return import.meta.env')();
    }
    catch {
        return undefined;
    }
}
export function IdpProvider({ children }) {
    const token = useGlobalStore((s) => s.token);
    const refreshToken = useGlobalStore((s) => s.refreshToken);
    const jti = useGlobalStore((s) => s.jti);
    const login = useGlobalStore((s) => s.login);
    const logout = useGlobalStore((s) => s.logout);
    const setAuthenticated = useGlobalStore((s) => s.setAuthenticated);
    const metaEnv = resolveMetaEnv();
    const basePath = metaEnv?.VITE_IDP_BASE_URL;
    const chan = useMemo(() => makeAuthChannel(), []);
    const client = useMemo(() => {
        const loginBroadcast = (accessToken, refreshToken, jti, remember) => {
            resetRefreshBlock();
            login(accessToken, refreshToken, jti, remember);
            try {
                chan.postMessage({
                    type: 'REFRESHED',
                    accessToken,
                    refreshToken,
                    jti,
                    exp: decodeExp(accessToken),
                    remember: !!remember,
                });
            }
            catch {
                /* ignore */
            }
        };
        const logoutBroadcast = () => {
            try {
                chan.postMessage({ type: 'LOGOUT' });
            }
            catch {
                /* ignore */
            }
            // Note: Do NOT reset refresh block here - keep it blocked until next successful login
            // This prevents refresh loops when stale cookies exist after logout
            logout();
        };
        // Token delivery: 'cookie' for HTTPS (production), 'json' for local HTTP dev
        // Set VITE_IDP_TOKEN_DELIVERY=json in .env.local for HTTP localhost
        const tokenDelivery = metaEnv?.VITE_IDP_TOKEN_DELIVERY ?? 'cookie';
        const c = createIdpClient({
            basePath,
            tokenDelivery,
            getAccessToken: () => useGlobalStore.getState().token ?? '',
            getRefreshToken: () => useGlobalStore.getState().refreshToken ?? '',
            getJti: () => useGlobalStore.getState().jti ?? '',
            login: loginBroadcast,
            logout: logoutBroadcast,
            getAudience: () => {
                const aud = resolveAudience();
                return aud === 'teetime-admin' ? 'teetime-admin' : 'teetime-web';
            },
        });
        return c;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [basePath]);
    // Multi-tab coordination
    useEffect(() => {
        const onMsg = (e) => {
            const msg = e.data;
            if (!msg)
                return;
            if (msg.type === 'REFRESHED') {
                try {
                    persist({ accessToken: msg.accessToken, refreshToken: msg.refreshToken, jti: msg.jti, exp: msg.exp }, msg.remember);
                }
                catch {
                    /* ignore */
                }
                login(msg.accessToken, msg.refreshToken, msg.jti, msg.remember);
            }
            else if (msg.type === 'LOGOUT') {
                logout();
            }
        };
        chan.addEventListener('message', onMsg);
        return () => chan.removeEventListener('message', onMsg);
    }, [chan, login, logout]);
    // Boot-time CSRF + token refresh regardless of remember setting
    useEffect(() => {
        let disposeTimer = null;
        const logoutBroadcast = () => {
            try {
                chan.postMessage({ type: 'LOGOUT' });
            }
            catch {
                /* ignore */
            }
            logout();
        };
        (async () => {
            const audience = resolveAudience() === 'teetime-admin' ? 'teetime-admin' : 'teetime-web';
            const boot = await authBoot(audience);
            if (boot.ok) {
                const t = boot.tokens;
                if (t) {
                    resetRefreshBlock(); // Clear any previous refresh block on successful auth
                    login(t.accessToken, t.refreshToken, t.jti, boot.remember);
                    try {
                        chan.postMessage({
                            type: 'REFRESHED',
                            accessToken: t.accessToken,
                            refreshToken: t.refreshToken,
                            jti: t.jti,
                            exp: t.exp,
                            remember: boot.remember,
                        });
                    }
                    catch {
                        /* ignore */
                    }
                    disposeTimer = scheduleProactiveRefresh(audience, t, (next, remember) => {
                        resetRefreshBlock(); // Clear block on successful proactive refresh
                        login(next.accessToken, next.refreshToken, next.jti, remember);
                        try {
                            chan.postMessage({
                                type: 'REFRESHED',
                                accessToken: next.accessToken,
                                refreshToken: next.refreshToken,
                                jti: next.jti,
                                exp: next.exp,
                                remember,
                            });
                        }
                        catch {
                            /* ignore */
                        }
                    }, boot.remember, logoutBroadcast);
                }
                else {
                    setAuthenticated();
                }
            }
            else if (boot.reason === 'unauthenticated') {
                // Only clear auth state for explicit unauthenticated failures.
                // Transient network failures should keep the current session state.
                logoutBroadcast();
            }
        })();
        return () => {
            try {
                if (disposeTimer)
                    disposeTimer();
            }
            catch {
                /* ignore */
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client]);
    // Visibility & network triggers
    useEffect(() => {
        const onVisible = async () => {
            if (document.visibilityState !== 'visible')
                return;
            const audience = resolveAudience() === 'teetime-admin' ? 'teetime-admin' : 'teetime-web';
            try {
                const t = useGlobalStore.getState().token;
                const expMs = t ? decodeExp(t) : 0;
                if (!t || expMs <= Date.now() + 120_000) {
                    const boot = await authBoot(audience);
                    if (boot.ok) {
                        const next = boot.tokens;
                        if (next) {
                            resetRefreshBlock(); // Clear block on successful visibility refresh
                            login(next.accessToken, next.refreshToken, next.jti, boot.remember);
                            try {
                                chan.postMessage({
                                    type: 'REFRESHED',
                                    accessToken: next.accessToken,
                                    refreshToken: next.refreshToken,
                                    jti: next.jti,
                                    exp: next.exp,
                                    remember: boot.remember,
                                });
                            }
                            catch {
                                /* ignore */
                            }
                        }
                        else {
                            setAuthenticated();
                        }
                    }
                    else if (boot.reason === 'unauthenticated') {
                        // Only clear stale auth state for explicit unauthenticated failures.
                        try {
                            chan.postMessage({ type: 'LOGOUT' });
                        }
                        catch {
                            /* ignore */
                        }
                        logout();
                    }
                }
            }
            catch {
                /* ignore */
            }
        };
        let lastOffline = null;
        const onOffline = () => {
            lastOffline = Date.now();
        };
        const onOnline = async () => {
            if (lastOffline && Date.now() - lastOffline > 300_000) {
                lastOffline = null;
                await onVisible();
            }
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', onOnline);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('offline', onOffline);
            window.removeEventListener('online', onOnline);
        };
    }, [chan, login, logout, setAuthenticated]);
    // Ensure provider updates when tokens change
    void token;
    void refreshToken;
    void jti;
    // Bind global APIs used by hooks-auth-web to the configured axios instance
    bindIdpClient(client);
    return _jsx(IdpContext.Provider, { value: client, children: children });
}
export function useOptionalIdp() {
    return useContext(IdpContext);
}
export function useIdp() {
    const ctx = useContext(IdpContext);
    if (!ctx)
        throw new Error('useIdp must be used within IdpProvider');
    return ctx;
}
//# sourceMappingURL=IdpProvider.js.map